import React, { useState, useEffect } from 'react';
import Header, { BreadcrumbItem } from '../components/Header';
import ContentReader from '../components/ContentReader';
import { progressService } from '../services/progressService';
import styles from '../styles/stepCommon.module.css';
import StepActions, { ActionItem } from '../components/StepActions';
import ComingSoonOverlay from '../components/ComingSoonOverlay';

interface Step10Props {
  user?: any;
  onLogin: () => void;
  onLogout: () => void;
}

const Step10: React.FC<Step10Props> = ({ user, onLogin, onLogout }) => {
  const [isContentReaderOpen, setIsContentReaderOpen] = useState(false);
  const [isGuidanceOpen, setIsGuidanceOpen] = useState(false);
  const [collapsedActions, setCollapsedActions] = useState<Set<number>>(new Set());
  // Define the original action structure
  const originalActions: ActionItem[] = [
    {
      id: 1,
      title: "Read the SAA Green Book about Step Ten",
      description: "Discover how daily personal inventory and prompt admission sustain clarity and connection in my recovery",
      completed: false,
      hasButton: true,
      buttonText: "Read Green Book",
      buttonAction: () => setIsContentReaderOpen(true)
    },
    {
      id: 2,
      title: "Read guidance on working Step Ten",
      description: "Read practical guidance on how to work Step Ten effectively.",
      completed: false,
      hasButton: true,
      buttonText: "Read Guidance",
      buttonAction: () => setIsGuidanceOpen(true)
    },
    {
      id: 3,
      title: "Take Step Ten: Continue to take personal inventory and promptly admit when wrong",
      description: "Practice ongoing self-examination and admit mistakes promptly",
      completed: false
    }
  ];

  const [actions, setActions] = useState<ActionItem[]>(originalActions);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false },
    { label: 'SAA', path: '/saa', active: false },
    { label: '12 Steps', path: '/saa/12-steps', active: false },
    { label: 'Step Ten', path: '/saa/12-steps/step-10', active: true }
  ];

  // Initialize progress tracking
  useEffect(() => {
    const initializeProgress = async () => {
      if (!user) return;
      
      setIsInitializing(true);
      
      try {
        // Initialize SAA progress
        await progressService.initializeProgress();
        
        // Fetch progress data
        const existingProgress = await progressService.getStepProgress('SAA', '12-steps', 10).catch(() => null);
        
        if (existingProgress) {
          const savedData = existingProgress.data || {};
          
          // Restore actions state
          if (savedData.actions) {
            // Merge saved actions with original actions to restore missing properties
            const mergedActions = originalActions.map(originalAction => {
              const savedAction = savedData.actions.find((a: any) => a.id === originalAction.id);
              if (savedAction) {
                return {
                  ...originalAction,
                  completed: savedAction.completed,
                  description: savedAction.description || originalAction.description
                };
              }
              return originalAction;
            });
            setActions(mergedActions);
          }
        }
      } catch (err) {
        console.error('Error initializing progress:', err);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeProgress();
  }, [user]);

  const getCurrentActionIndex = () => {
    return actions.findIndex(action => !action.completed);
  };

  const isActionAvailable = (actionIndex: number) => {
    if (actionIndex === 0) return true;
    return actions.slice(0, actionIndex).every(a => a.completed);
  };

  const handleActionChange = async (updatedActions: ActionItem[]) => {
    // For other actions, save to backend
    if (!user) {
      setError('User not logged in. Please log in.');
      return;
    }
    
    try {
      // Update local state
      setActions(updatedActions);
      
      // Save to backend
      const allCompleted = updatedActions.every(action => action.completed);
      await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 10,
        step_title: 'Step Ten',
        completed: allCompleted,
        data: {
          actions: updatedActions
        }
      });
      
      // Update status note
      const completedCount = updatedActions.filter(a => a.completed).length;
      await progressService.updateStatus({
        program: 'SAA',
        track: '12-steps',
        status_note: `Working Step Ten - ${completedCount} of 3 actions completed`,
        progress_milestone: allCompleted ? 'step10_completed' : 'step10_in_progress'
      });
    } catch (err) {
      console.error('Error updating action progress:', err);
      setError('Failed to save progress. Please try again.');
    }
  };

  // Check if all actions are completed
  const allCompleted = actions.every(action => action.completed);

  if (error) {
    return (
      <div className={styles.step10Page}>
        <Header 
          breadcrumbs={breadcrumbs}
          user={user}
          onLogout={onLogout}
          onLogin={onLogin}
        />
        
        <main className={styles.mainContent}>
          <div className={styles.pageHeader}>
            <h1 className={styles.stepTitle}>Step Ten</h1>
            <p className={styles.stepSubtitle}>
              Continued to take personal inventory and when we were wrong promptly admitted it.
            </p>
          </div>
          <div className={styles.errorMessage}>
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>Refresh Page</button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.stepPage}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={user}
        onLogout={onLogout}
        onLogin={onLogin}
      />
      
      <main className={styles.mainContent}>
        <div className={styles.pageHeader}>
          <h1 className={styles.stepTitle}>Step Ten</h1>
          <p className={styles.stepSubtitle}>
            Continued to take personal inventory and when we were wrong promptly admitted it.
          </p>
        </div>

        {isInitializing && (
          <div className={styles.loadingMessage}>
            <p>Loading your progress...</p>
          </div>
        )}

        {error && (
          <div className={styles.errorMessage}>
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>Refresh Page</button>
          </div>
        )}

        {!user && (
          <div className={styles.actionsContainer}>
            <h2 className={styles.actionsTitle}>Actions to Complete Step Ten</h2>
            <p className={styles.actionsDescription}>
              Login to track your progress through Step Ten.
            </p>
            <button onClick={onLogin} className={styles.actionButton}>
              Login or Create Account
            </button>
          </div>
        )}

        {!isInitializing && !error && user && (
          <div className={styles.actionsContainer}>
            <h2 className={styles.actionsTitle}>Actions to Complete Step Ten</h2>
            <p className={styles.actionsDescription}>
              Complete each action in order. Check the box when you've finished each action.
            </p>
            
            <StepActions
              actions={actions}
              onActionChange={handleActionChange}
              stepNumber={10}
            />

            {allCompleted && (
              <>
                <div className={styles.completionMessage}>
                  <h3>🎉 Congratulations!</h3>
                  <p>You have completed all actions for Step Ten and can move on to Step Eleven.</p>
                </div>
                <div className={styles.stepNavigation}>
                  <div className={styles.navigationLinks}>
                    <a href="/saa/12-steps/step-9" className={styles.prevStepLink}>
                      {'<<'} Step Nine
                    </a>
                    <span></span>
                    <a href="/saa/12-steps/step-11" className={styles.nextStepLink}>
                      Step Eleven {'>>'}
                    </a>
                  </div>
                </div>
              </>
            )}

            {!allCompleted && (
              <div className={styles.stepNavigation}>
                <div className={styles.navigationLinks}>
                  <a href="/saa/12-steps/step-9" className={styles.prevStepLink}>
                    {'<<'} Step Nine
                  </a>
                  <span></span>
                  <a href="/saa/12-steps/step-11" className={styles.nextStepLink}>
                    Step Eleven {'>>'}
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* ContentReader for Green Book */}
      <ContentReader
        isOpen={isContentReaderOpen}
        onClose={() => setIsContentReaderOpen(false)}
        filePath="/content/010_CHAPTER_THREE.md"
        startTag="<<step-ten start>>"
        endTag="<<step-ten end>>"
      />

      {/* ContentReader for How to Work Step Nine */}
      <ContentReader
        isOpen={isGuidanceOpen}
        onClose={() => setIsGuidanceOpen(false)}
        filePath="/content/Step_10_Guidance.md"
        startTag="<<working-step-ten start>>"
        endTag="<<working-step-ten end>>"
      />

      {/* Coming Soon Overlay */}
      <ComingSoonOverlay 
        message="Step Ten - Coming Soon"
        isVisible={true}
      />
    </div>
  );
};

export default Step10; 